import React, { useMemo, useState, createElement } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { GTFonts, useTheme } from '../../theme';
import { fs, s, v } from '../../utils/libs';
import type { FormSelectFieldProps } from './FormSelectField.types';

/**
 * Web: one native &lt;select&gt; with outline styling (matches DatePickerField / Paper inputs).
 * Avoids @react-native-picker rendering a nested &lt;select&gt; inside a bordered View.
 */
const FormSelectField: React.FC<FormSelectFieldProps> = ({
  label,
  value,
  onValueChange,
  options,
  disabled = false,
  placeholder = 'Select',
  loading = false,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [focused, setFocused] = useState(false);

  const selectStyle = useMemo(
    () => ({
      width: '100%' as const,
      boxSizing: 'border-box' as const,
      paddingTop: v(8),
      paddingBottom: v(8),
      paddingLeft: s(12),
      paddingRight: s(36),
      fontSize: fs(14),
      fontFamily: GTFonts.regular,
      lineHeight: `${fs(18)}px`,
      borderRadius: theme.borderRadius.md,
      borderWidth: 1,
      borderStyle: 'solid' as const,
      borderColor: focused ? theme.colors.primary : theme.colors.border,
      backgroundColor: theme.colors.surface,
      color: theme.colors.text,
      minHeight: v(56),
      appearance: 'none' as const,
      WebkitAppearance: 'none' as const,
      MozAppearance: 'none' as const,
      cursor: disabled || loading ? 'not-allowed' : 'pointer',
      opacity: disabled || loading ? 0.55 : 1,
      outline: 'none',
      backgroundImage: loading
        ? 'none'
        : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 12px center',
      marginBottom: v(6),
    }),
    [
      disabled,
      focused,
      loading,
      theme.borderRadius.md,
      theme.colors.border,
      theme.colors.primary,
      theme.colors.surface,
      theme.colors.text,
    ],
  );

  const optionNodes = [
    createElement('option', { key: '__empty', value: '' }, placeholder),
    ...options.map(opt =>
      createElement(
        'option',
        {
          key: opt.value,
          value: opt.value,
          disabled: opt.enabled === false,
        },
        opt.label,
      ),
    ),
  ];

  return (
    <View style={styles.pickerSection}>
      <Text style={styles.pickerLabel}>{label}</Text>
      <View style={styles.selectWrapper}>
        {createElement('select', {
          value: value || '',
          disabled: disabled || loading,
          onChange: (e: { target: { value: string } }) =>
            onValueChange(String(e.target.value)),
          onFocus: () => setFocused(true),
          onBlur: () => setFocused(false),
          style: selectStyle,
          children: optionNodes,
        })}
        {loading && (
          <View style={styles.chevronOverlay} pointerEvents="none">
            <ActivityIndicator size="small" color={theme.colors.primary} />
          </View>
        )}
      </View>
    </View>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    pickerSection: {
      gap: v(4),
    },
    pickerLabel: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    selectWrapper: {
      position: 'relative',
    },
    chevronOverlay: {
      position: 'absolute',
      right: s(12),
      top: 0,
      bottom: v(6),
      justifyContent: 'center',
      alignItems: 'center',
    },
  });

export default FormSelectField;
